/**
 * @mergeTarget
 * @module Src/Parameters/String
 */
import { LisioProfilErrorCodes } from "../profil-error-codes.js";
import { LisioParameter } from "./lisio-parameter.js";
import { CursorSizeOptions } from "./stringOptionsEnums/cursor-size-options.js";
import { DatltonismOptions } from "./stringOptionsEnums/daltonism-options.js";
import { DeeplTranslationOptions } from "./stringOptionsEnums/deepl-translation-options.js";
import { FontFamilyOptions } from "./stringOptionsEnums/font-family-options.js";
import { GoogleTranslationOptions } from "./stringOptionsEnums/google-translation-options.js";
import { TextAlignOptions } from "./stringOptionsEnums/text-align-options.js";
import { ThemeOptions } from "./stringOptionsEnums/theme-options.js";
/**
 * Enumerator for all string parameters availables in Lisio.\
 * Please names new parameters as following :
 * ```
 * MY_NAME : "my_name"
 * ```
 * @source
 */
var LisioStringParameterNames;
(function (LisioStringParameterNames) {
    LisioStringParameterNames["THEME"] = "theme";
    LisioStringParameterNames["DALTONISM"] = "daltonism";
    LisioStringParameterNames["FONT_FAMILY"] = "font_family";
    LisioStringParameterNames["TEXT_ALIGN"] = "text_align";
    LisioStringParameterNames["CURSOR_SIZE"] = "cursor_size";
    LisioStringParameterNames["GOOGLE_TRANSLATION"] = "google_translation";
    LisioStringParameterNames["DEEPL_TRANSLATION"] = "deepl_translation";
    LisioStringParameterNames["SPEECH_SYNTHESIS_VOICE"] = "speech_synthesis_voice";
    LisioStringParameterNames["UNDERLINE_RED"] = "underline_red";
    LisioStringParameterNames["UNDERLINE_GREEN"] = "underline_green";
    LisioStringParameterNames["UNDERLINE_BLUE"] = "underline_blue";
})(LisioStringParameterNames || (LisioStringParameterNames = {}));
/**
 * Correspondance between parameter and options values.\
 * Add here you options for a given string parameter
 * @source
 */
const parametersWithOptions = new Map([
    [LisioStringParameterNames.THEME, ThemeOptions],
    [LisioStringParameterNames.CURSOR_SIZE, CursorSizeOptions],
    [LisioStringParameterNames.DALTONISM, DatltonismOptions],
    [LisioStringParameterNames.DEEPL_TRANSLATION, DeeplTranslationOptions],
    [LisioStringParameterNames.GOOGLE_TRANSLATION, GoogleTranslationOptions],
    [LisioStringParameterNames.FONT_FAMILY, FontFamilyOptions],
    [LisioStringParameterNames.TEXT_ALIGN, TextAlignOptions],
]);
/**
 * Set of all parameter string which values cannot be determined.\
 * Add here if your string parameter doesn't have determined values
 * @source
 */
const undeterminedStringParameters = new Set([
    LisioStringParameterNames.UNDERLINE_RED,
    LisioStringParameterNames.UNDERLINE_GREEN,
    LisioStringParameterNames.UNDERLINE_BLUE,
]);
/**
 * Function to check if parameter and value are corresponding
 * @param {LisioStringParameterNames} name - Parameter name
 * @param {string | StringParametersOptions} value - Parameter value
 * @returns Returns if value corresponding with parameter
 * @source
 */
function checkIfValueCorrespondingWithParameter(name, value) {
    if (!undeterminedStringParameters.has(name)) {
        const options = parametersWithOptions.get(name);
        if (options == undefined ||
            (options != undefined && !(value.toUpperCase() in options))) {
            return false;
        }
    }
    return true;
}
/**
 * ## How to create a new (string) parameter ? Part 2
 *
 * To create a new string parameter is very easy. To begin, choose a revelant name and add it to {@link LisioStringParameterNames | LisioStringParameterNames}.\
 * If you parameter has determined answers, please create a new enumerator like {@link Src/Parameters/String/Options.ThemeOptions | ThemeOptions} to prevent errors and it to {@link StringParametersOptions | StringParametersOptions}.
 * Next you have to adapt method {@link checkIfValueCorrespondingWithParameter | checkIfValueCorrespondingWithParameter} :
 *  * If you parameter has determined answers : add a new entry in {@link parametersWithOptions | parametersWithOptions}
 *  * Else add a new entry in {@link undeterminedStringParameters | undeterminedStringParameters}
 *
 * ## How to create a new (string) parameter ? Part 2
 *
 * If you want to rename it, choose a new revelant name and modify the old one in {@link LisioStringParameterNames | LisioStringParameterNames}.\
 * If you want change his type, see "How to delete" for current parameter and "How to create a new parameter" for new parameter.\
 *
 * ## How to delete a (string) parameter ? Part 2
 *
 * Delete his name from {@link LisioStringParameterNames | LisioStringParameterNames}.\
 *
 * ## Documentation
 *
 * Class representing a string parameter extending LisioParameter.\
 * It aims to centralize all the logic of string parameter.\
 * To execute his responsability this class does :
 *  * Saves name and value of string parameter
 *  * Check if a value is associated with his parameter
 */
class LisioStringParameter extends LisioParameter {
    /**
     * Protected attribute to store value of parameter
     * @source
     */
    _value;
    /**
     * Protected attribute to store default value of parameter
     * @source
     */
    _defaultValue;
    /**
     * Getter for attribute {@link _value | _value}
     * @returns Returns _value attribute
     * @source
     */
    get value() {
        return this._value;
    }
    /**
     * Getter for attribute {@link _defaultValue | _defaultValue}
     * @returns Returns _defaultValue attribute
     * @source
     */
    get defaultValue() {
        return this._defaultValue;
    }
    /**
     * Constructor of class {@link LisioStringParameter | LisioStringParameter}
     * @param {LisioStringParameterNames} name - Parameter name
     * @param {string | StringParametersOptions} value - Parameter value
     * @throws If parameter doesn't exist or value not corresponding.\
     * See {@link Src/Utils.LisioProfilErrorCodes.WRONG_STRING_PARAMETER | LisioProfilErrorCodes.WRONG_STRING_PARAMETER}
     * @source
     */
    constructor(name, value) {
        super(name);
        if (checkIfValueCorrespondingWithParameter(name, value)) {
            this._value = value;
        }
        else {
            throw new Error(`Code : ${LisioProfilErrorCodes.WRONG_STRING_PARAMETER}. Parameter ${name} may not exists or value ${value} not in options`);
        }
        this._defaultValue =
            name != LisioStringParameterNames.UNDERLINE_RED &&
                name != LisioStringParameterNames.UNDERLINE_GREEN &&
                name != LisioStringParameterNames.UNDERLINE_BLUE
                ? "default"
                : "";
    }
}
export { LisioStringParameter, LisioStringParameterNames };
